import { useCallback, useEffect, useState } from "react";
import { RefreshCw, LogOut, Download, ChevronLeft, ChevronRight, X, Lock, Search } from "lucide-react";
import { leadsApi } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { WhatsAppIcon } from "@/components/Cta";

const STATUS_OPTIONS = ["新客户", "已联系", "已回复", "有兴趣", "跟进中", "无效", "已成交"];
const TOPICS = ["TikTok Shop 开店", "一件代发", "无需自行囤货", "电商副业", "已有产品 / 商家", "Shopee / Lazada 转 TikTok Shop", "其他"];
const SITUATIONS = ["还没开始", "已经有产品", "已经有 Shopee / Lazada", "有实体店", "已有 TikTok Shop", "只是先了解"];

const STATUS_CLS = {
  新客户: "bg-lime/30 text-forest",
  已联系: "bg-forest/10 text-forest",
  已回复: "bg-blue-100 text-blue-800",
  有兴趣: "bg-amber-100 text-amber-800",
  跟进中: "bg-orange-100 text-orange-800",
  无效: "bg-ink/10 text-ink/50",
  已成交: "bg-forest text-paper",
};

const dstr = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return d.toLocaleDateString("en-CA");
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
};

const waPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (!d) return null;
  if (d.startsWith("60")) return d;
  if (d.startsWith("0")) return "60" + d.slice(1);
  return d;
};

const pct = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v}%`);

export default function AdminLeads() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("tks_admin_code") || "");
  const [codeInput, setCodeInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("clicks");
  const [stats, setStats] = useState(null);
  const [clicksData, setClicksData] = useState({ clicks: [], total: 0, page: 1, per_page: 20 });
  const [leadsData, setLeadsData] = useState({ leads: [], total: 0, page: 1, per_page: 20 });
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState(dstr(7));
  const [customTo, setCustomTo] = useState(dstr(0));
  const [source, setSource] = useState("");
  const [topic, setTopic] = useState("");
  const [situation, setSituation] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [clicksPage, setClicksPage] = useState(1);
  const [leadsPage, setLeadsPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [detail, setDetail] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("新客户");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setLeadsPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rangeParams = useCallback(() => {
    if (range === "today") return { date_from: dstr(0), date_to: dstr(0) };
    if (range === "yesterday") return { date_from: dstr(1), date_to: dstr(1) };
    if (range === "7d") return { date_from: dstr(6), date_to: dstr(0) };
    if (range === "30d") return { date_from: dstr(29), date_to: dstr(0) };
    if (range === "custom" && customFrom && customTo) return { date_from: customFrom, date_to: customTo };
    return {};
  }, [range, customFrom, customTo]);

  const sharedParams = useCallback(
    () => ({ ...rangeParams(), source: source || undefined }),
    [rangeParams, source]
  );

  const loadStats = useCallback(async (c = authed) => {
    if (!c) return;
    try {
      setStats(await leadsApi.stats(c));
    } catch {
      setAuthed("");
      sessionStorage.removeItem("tks_admin_code");
    }
  }, [authed]);

  const loadClicks = useCallback(
    async (c = authed, p = clicksPage, pp = perPage) => {
      if (!c) return;
      setLoading(true);
      try {
        setClicksData(await leadsApi.clicks(c, { ...sharedParams(), page: p, per_page: pp }));
      } catch {
        /* keep old data on transient errors */
      } finally {
        setLoading(false);
      }
    },
    [authed, clicksPage, perPage, sharedParams]
  );

  const loadLeads = useCallback(
    async (c = authed, p = leadsPage, pp = perPage) => {
      if (!c) return;
      setLoading(true);
      try {
        setLeadsData(await leadsApi.list(c, {
          ...sharedParams(),
          topic: topic || undefined,
          situation: situation || undefined,
          search: search || undefined,
          page: p,
          per_page: pp,
        }));
      } catch {
        /* keep old data on transient errors */
      } finally {
        setLoading(false);
      }
    },
    [authed, leadsPage, perPage, sharedParams, topic, situation, search]
  );

  useEffect(() => {
    if (!authed) return;
    loadStats();
    if (tab === "clicks") loadClicks();
    else loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, clicksPage, leadsPage, perPage, range, customFrom, customTo, source, topic, situation, search]);

  const unlock = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await leadsApi.stats(codeInput.trim());
      sessionStorage.setItem("tks_admin_code", codeInput.trim());
      setAuthed(codeInput.trim());
    } catch {
      setLoginError("密码错误，请重试");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("tks_admin_code");
    setAuthed("");
  };

  const openDetail = (lead) => {
    setDetail(lead);
    setStatusDraft(lead.status || "新客户");
    setNoteDraft(lead.notes || "");
  };

  const saveDetail = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await leadsApi.update(authed, detail.id, { status: statusDraft, notes: noteDraft });
      setDetail(null);
      loadLeads();
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      let csv;
      if (tab === "clicks") {
        const res = await leadsApi.clicks(authed, { ...sharedParams(), page: 1, per_page: 500 });
        const header = ["点击时间", "来源渠道", "来源页面", "按钮位置", "CTA 文案", "utm_source", "utm_medium", "utm_campaign", "gclid", "fbclid"];
        const rows = res.clicks.map((c) => [
          c.created_at, c.source_channel_label, c.page_path || "", c.button_location || "", c.cta_text || "",
          c.utm_source || "", c.utm_medium || "", c.utm_campaign || "", c.gclid || "", c.fbclid || "",
        ]);
        csv = "﻿" + [header, ...rows].map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      } else {
        const res = await leadsApi.list(authed, {
          ...sharedParams(), topic: topic || undefined, situation: situation || undefined, search: search || undefined, page: 1, per_page: 500,
        });
        const header = ["提交时间", "姓名", "年龄", "手机号码", "咨询类型", "目前情况", "来源", "UTM Campaign", "gclid", "fbclid", "状态", "备注"];
        const rows = res.leads.map((l) => [
          l.created_at, l.name, l.age, l.phone || "", l.topic, l.situation || "",
          l.source_channel_label, l.utm_campaign || "", l.gclid || "", l.fbclid || "",
          l.status || "新客户", (l.notes || "").replace(/[\r\n]+/g, " "),
        ]);
        csv = "﻿" + [header, ...rows].map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      }
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tkshopmy-${tab === "clicks" ? "whatsapp-clicks" : "leads"}-${dstr(0)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const activeData = tab === "clicks" ? { total: clicksData.total, page: clicksData.page } : { total: leadsData.total, page: leadsData.page };
  const activePage = tab === "clicks" ? clicksPage : leadsPage;
  const setActivePage = tab === "clicks" ? setClicksPage : setLeadsPage;
  const totalPages = Math.max(1, Math.ceil(activeData.total / perPage));

  /* ---------- Login gate ---------- */
  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest px-5">
        <Seo title="客户资料后台" description="内部管理页面" path="/admin/leads" noindex />
        <form onSubmit={unlock} className="w-full max-w-sm rounded-3xl bg-paper p-8 shadow-2xl" data-testid="admin-login-form">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-lime">
            <Lock size={20} />
          </div>
          <h1 className="mt-5 font-sans text-xl font-bold text-ink">客户资料后台</h1>
          <p className="mt-1 text-sm text-ink/55">请输入管理员密码查看数据。</p>
          <input
            type="password"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            placeholder="管理员密码"
            className="mt-5 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none focus:border-forest"
            data-testid="admin-login-input"
          />
          {loginError && <p className="mt-2 text-sm text-red-700" data-testid="admin-login-error">{loginError}</p>}
          <button type="submit" className="mt-4 w-full rounded-full bg-forest py-3 text-sm font-semibold text-paper hover:bg-ink" data-testid="admin-login-submit">
            进入后台
          </button>
        </form>
      </div>
    );
  }

  const cards = stats
    ? [
        { label: "今日 WhatsApp 点击", value: stats.click_today, sub: `昨日 ${stats.click_yesterday} · ${pct(stats.click_today_vs_yesterday_pct)}`, tid: "stat-today" },
        { label: "昨日 WhatsApp 点击", value: stats.click_yesterday, tid: "stat-yesterday" },
        { label: "过去 7 天", value: stats.click_last7, sub: `上周对比 ${pct(stats.click_week_vs_last_week_pct)}`, tid: "stat-7d" },
        { label: "过去 30 天", value: stats.click_last30, sub: `日均 ${stats.click_avg_per_day_30}`, tid: "stat-30d" },
        { label: "Google Ads WhatsApp 点击", value: stats.click_google, tid: "stat-google" },
        { label: "Facebook / Meta WhatsApp 点击", value: stats.click_meta, tid: "stat-meta" },
        { label: "Organic / Direct 点击", value: stats.click_organic, tid: "stat-organic" },
        { label: "累计 WhatsApp 点击", value: stats.click_total, tid: "stat-total" },
      ]
    : [];

  const sources = stats
    ? [
        { name: "Google Ads", clicks: stats.click_google, tid: "source-google" },
        { name: "Facebook / Meta", clicks: stats.click_meta, tid: "source-meta" },
        { name: "Organic / Direct", clicks: stats.click_organic, tid: "source-organic" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-paper pb-16">
      <Seo title="客户资料后台" description="内部管理页面" path="/admin/leads" noindex />

      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-ink/10 bg-forest text-paper">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <p className="font-display text-lg font-semibold">TKShopMY<span className="text-lime">.</span></p>
            <span className="rounded-full bg-paper/10 px-3 py-1 text-xs font-medium text-paper/80">WhatsApp 咨询统计</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadStats(); tab === "clicks" ? loadClicks() : loadLeads(); }} className="flex items-center gap-1.5 rounded-full bg-paper/10 px-3.5 py-2 text-xs font-semibold hover:bg-paper/20" data-testid="admin-refresh">
              <RefreshCw size={13} /> 刷新
            </button>
            <button onClick={exportCsv} disabled={exporting} className="flex items-center gap-1.5 rounded-full bg-lime px-3.5 py-2 text-xs font-semibold text-ink hover:brightness-95 disabled:opacity-60" data-testid="admin-export-csv">
              <Download size={13} /> {exporting ? "导出中…" : "导出 CSV"}
            </button>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-full bg-paper/10 px-3.5 py-2 text-xs font-semibold hover:bg-paper/20" data-testid="admin-logout">
              <LogOut size={13} /> 退出
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-8">
        {/* Stats cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-stats">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={c.tid}>
              <p className="text-xs font-medium text-ink/50">{c.label}</p>
              <p className="mt-1.5 font-display text-2xl font-semibold text-ink">{stats ? c.value : "…"}</p>
              {c.sub && <p className="mt-1 text-xs text-ink/45">{c.sub}</p>}
            </div>
          ))}
        </div>

        {/* Today report + source effectiveness */}
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <div className="rounded-2xl border border-ink/10 bg-white p-5" data-testid="today-report">
            <p className="text-sm font-bold text-ink">今日 WhatsApp 点击（马来西亚时间）</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-paper px-3 py-1.5 font-medium text-ink/70">Google Ads：{stats?.click_today_google ?? "…"}</span>
              <span className="rounded-full bg-paper px-3 py-1.5 font-medium text-ink/70">Facebook / Meta：{stats?.click_today_meta ?? "…"}</span>
              <span className="rounded-full bg-paper px-3 py-1.5 font-medium text-ink/70">Organic / Direct：{stats?.click_today_organic ?? "…"}</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-paper py-3">
                <p className="font-display text-xl font-semibold">{stats?.click_today ?? "…"}</p>
                <p className="mt-0.5 text-xs text-ink/50">今日点击</p>
              </div>
              <div className="rounded-xl bg-paper py-3">
                <p className="font-display text-xl font-semibold">{stats?.click_yesterday ?? "…"}</p>
                <p className="mt-0.5 text-xs text-ink/50">昨日点击</p>
              </div>
              <div className="rounded-xl bg-paper py-3">
                <p className="font-display text-xl font-semibold text-forest">{pct(stats?.click_today_vs_yesterday_pct)}</p>
                <p className="mt-0.5 text-xs text-ink/50">今日 vs 昨日</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-ink/10 bg-white p-5" data-testid="source-stats">
            <p className="text-sm font-bold text-ink">广告效果统计（全部时间）</p>
            <div className="mt-3 space-y-2.5">
              {sources.map((s) => (
                <div key={s.name} className="flex items-center justify-between rounded-xl bg-paper px-4 py-3" data-testid={s.tid}>
                  <p className="text-sm font-semibold text-ink">{s.name}</p>
                  <div className="flex items-center gap-4 text-xs text-ink/60">
                    <span>WhatsApp 点击 <b className="text-ink">{s.clicks}</b></span>
                    <span>占比 <b className="text-forest">{stats && stats.click_total ? `${Math.round((s.clicks / stats.click_total) * 100)}%` : "—"}</b></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2" data-testid="admin-tabs">
          <button
            onClick={() => setTab("clicks")}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "clicks" ? "bg-forest text-paper" : "bg-white text-ink/60 border border-ink/10 hover:text-ink"}`}
            data-testid="tab-clicks"
          >
            WhatsApp 点击记录
          </button>
          <button
            onClick={() => setTab("leads")}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "leads" ? "bg-forest text-paper" : "bg-white text-ink/60 border border-ink/10 hover:text-ink"}`}
            data-testid="tab-leads"
          >
            历史表单资料（旧）
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 rounded-2xl border border-ink/10 bg-white p-4" data-testid="admin-filters">
          <div className="flex flex-wrap items-center gap-2">
            <select value={range} onChange={(e) => { setRange(e.target.value); setClicksPage(1); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-4 py-2 text-sm outline-none focus:border-forest" data-testid="filter-date">
              <option value="all">全部日期</option>
              <option value="today">今天</option>
              <option value="yesterday">昨天</option>
              <option value="7d">过去 7 天</option>
              <option value="30d">过去 30 天</option>
              <option value="custom">自定义日期</option>
            </select>
            {range === "custom" && (
              <>
                <input type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); setClicksPage(1); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-3 py-2 text-sm" data-testid="filter-date-from" />
                <span className="text-xs text-ink/40">至</span>
                <input type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); setClicksPage(1); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-3 py-2 text-sm" data-testid="filter-date-to" />
              </>
            )}
            <select value={source} onChange={(e) => { setSource(e.target.value); setClicksPage(1); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-4 py-2 text-sm outline-none focus:border-forest" data-testid="filter-source">
              <option value="">全部来源</option>
              <option value="google">Google Ads</option>
              <option value="meta">Facebook / Meta</option>
              <option value="organic">Organic / Direct</option>
            </select>
            {tab === "leads" && (
              <>
                <select value={topic} onChange={(e) => { setTopic(e.target.value); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-4 py-2 text-sm outline-none focus:border-forest" data-testid="filter-topic">
                  <option value="">全部咨询类型</option>
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={situation} onChange={(e) => { setSituation(e.target.value); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-paper px-4 py-2 text-sm outline-none focus:border-forest" data-testid="filter-situation">
                  <option value="">全部目前情况</option>
                  {SITUATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="relative min-w-[180px] flex-1">
                  <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/35" />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="搜索姓名 / 手机 / 咨询类型"
                    className="w-full rounded-full border border-ink/15 bg-paper py-2 pl-9 pr-4 text-sm outline-none focus:border-forest"
                    data-testid="filter-search"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* CLICKS TAB */}
        {tab === "clicks" && (
          <>
            <div className="mt-4 hidden overflow-x-auto rounded-2xl border border-ink/10 bg-white md:block" data-testid="clicks-table">
              <table className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="border-b border-ink/10 bg-paper text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                    <th className="px-4 py-3">点击时间</th>
                    <th className="px-4 py-3">来源渠道</th>
                    <th className="px-4 py-3">来源页面</th>
                    <th className="px-4 py-3">按钮位置</th>
                    <th className="px-4 py-3">utm_source</th>
                    <th className="px-4 py-3">gclid</th>
                    <th className="px-4 py-3">fbclid</th>
                  </tr>
                </thead>
                <tbody>
                  {clicksData.clicks.map((c) => (
                    <tr key={c.id} className="border-b border-ink/5 transition-colors hover:bg-paper/60" data-testid={`click-row-${c.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-ink/60">{fmtTime(c.created_at)}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-forest/10 px-2.5 py-1 text-xs font-medium text-forest">{c.source_channel_label}</span></td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-ink/70" title={c.page_url}>{c.page_path || "—"}</td>
                      <td className="px-4 py-3 text-ink/60">{c.button_location || "—"}</td>
                      <td className="px-4 py-3 text-ink/60">{c.utm_source || "—"}</td>
                      <td className="max-w-[110px] truncate px-4 py-3 text-xs text-ink/50" title={c.gclid || ""}>{c.gclid || "—"}</td>
                      <td className="max-w-[110px] truncate px-4 py-3 text-xs text-ink/50" title={c.fbclid || ""}>{c.fbclid || "—"}</td>
                    </tr>
                  ))}
                  {!clicksData.clicks.length && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-ink/40">{loading ? "加载中…" : "当前筛选没有点击记录"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 space-y-3 md:hidden" data-testid="clicks-cards">
              {clicksData.clicks.map((c) => (
                <div key={c.id} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={`click-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-ink">{c.button_location || "—"}</p>
                    <span className="rounded-full bg-forest/10 px-2.5 py-1 text-xs font-medium text-forest">{c.source_channel_label}</span>
                  </div>
                  <p className="mt-1 text-xs text-ink/50">{fmtTime(c.created_at)} · {c.page_path || "—"}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-ink/45">
                    {c.utm_source && <span className="rounded-full bg-paper px-2 py-0.5">utm: {c.utm_source}</span>}
                    {c.gclid && <span className="rounded-full bg-paper px-2 py-0.5">gclid ✓</span>}
                    {c.fbclid && <span className="rounded-full bg-paper px-2 py-0.5">fbclid ✓</span>}
                  </div>
                </div>
              ))}
              {!clicksData.clicks.length && <p className="py-10 text-center text-sm text-ink/40">{loading ? "加载中…" : "当前筛选没有点击记录"}</p>}
            </div>
          </>
        )}

        {/* LEADS TAB (historical form data) */}
        {tab === "leads" && (
          <>
            <div className="mt-4 hidden overflow-hidden rounded-2xl border border-ink/10 bg-white md:block" data-testid="leads-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ink/10 bg-paper text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                    <th className="px-4 py-3">提交时间</th>
                    <th className="px-4 py-3">姓名</th>
                    <th className="px-4 py-3">咨询类型</th>
                    <th className="px-4 py-3">目前情况</th>
                    <th className="px-4 py-3">来源</th>
                    <th className="px-4 py-3">状态</th>
                    <th className="px-4 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {leadsData.leads.map((l) => (
                    <tr key={l.id} className="border-b border-ink/5 transition-colors hover:bg-paper/60" data-testid={`lead-row-${l.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-ink/60">{fmtTime(l.created_at)}</td>
                      <td className="px-4 py-3 font-semibold text-ink">{l.name}<span className="ml-1.5 text-xs font-normal text-ink/40">{l.age}岁</span></td>
                      <td className="px-4 py-3 text-ink/70">{l.topic}</td>
                      <td className="px-4 py-3 text-ink/60">{l.situation || "—"}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-forest/10 px-2.5 py-1 text-xs font-medium text-forest">{l.source_channel_label}</span></td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLS[l.status] || STATUS_CLS["新客户"]}`}>{l.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {waPhone(l.phone) && (
                            <button onClick={() => window.open(`https://wa.me/${waPhone(l.phone)}`, "_blank", "noopener,noreferrer")} className="rounded-full bg-forest/10 p-2 text-forest hover:bg-forest hover:text-paper" title="WhatsApp 联系" data-testid={`lead-wa-${l.id}`}>
                              <WhatsAppIcon size={13} />
                            </button>
                          )}
                          <button onClick={() => openDetail(l)} className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-forest hover:text-forest" data-testid={`lead-detail-${l.id}`}>
                            详情
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!leadsData.leads.length && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-ink/40">{loading ? "加载中…" : "当前筛选没有资料"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 space-y-3 md:hidden" data-testid="leads-cards">
              {leadsData.leads.map((l) => (
                <div key={l.id} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={`lead-card-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-ink">{l.name} <span className="text-xs font-normal text-ink/40">{l.age}岁</span></p>
                      <p className="mt-0.5 text-xs text-ink/50">{fmtTime(l.created_at)} · {l.source_channel_label}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLS[l.status] || STATUS_CLS["新客户"]}`}>{l.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-ink/70">{l.topic}{l.situation ? ` · ${l.situation}` : ""}</p>
                  <div className="mt-3 flex items-center gap-2">
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/70" data-testid={`lead-tel-${l.id}`}>{l.phone}</a>
                    )}
                    {waPhone(l.phone) && (
                      <button onClick={() => window.open(`https://wa.me/${waPhone(l.phone)}`, "_blank", "noopener,noreferrer")} className="flex items-center gap-1.5 rounded-full bg-forest px-3 py-1.5 text-xs font-semibold text-paper" data-testid={`lead-wa-m-${l.id}`}>
                        <WhatsAppIcon size={12} /> WhatsApp 联系
                      </button>
                    )}
                    <button onClick={() => openDetail(l)} className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/70" data-testid={`lead-detail-m-${l.id}`}>详情</button>
                  </div>
                </div>
              ))}
              {!leadsData.leads.length && <p className="py-10 text-center text-sm text-ink/40">{loading ? "加载中…" : "当前筛选没有资料"}</p>}
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-pagination">
          <p className="text-xs text-ink/50">共 {activeData.total} 条 · 第 {activePage} / {totalPages} 页</p>
          <div className="flex items-center gap-2">
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setClicksPage(1); setLeadsPage(1); }} className="rounded-full border border-ink/15 bg-white px-3 py-2 text-xs font-medium" data-testid="per-page-select">
              <option value={20}>每页 20 条</option>
              <option value={50}>每页 50 条</option>
              <option value={100}>每页 100 条</option>
            </select>
            <button onClick={() => setActivePage((p) => Math.max(1, p - 1))} disabled={activePage <= 1} className="rounded-full border border-ink/15 bg-white p-2 disabled:opacity-40" data-testid="page-prev"><ChevronLeft size={15} /></button>
            <button onClick={() => setActivePage((p) => Math.min(totalPages, p + 1))} disabled={activePage >= totalPages} className="rounded-full border border-ink/15 bg-white p-2 disabled:opacity-40" data-testid="page-next"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>

      {/* Lead detail modal (historical leads only) */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6" data-testid="lead-detail-modal">
          <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-paper p-6 md:max-w-lg md:rounded-3xl">
            <button onClick={() => setDetail(null)} className="absolute right-4 top-4 rounded-full border border-ink/10 bg-white p-2 text-ink/50 hover:text-ink" data-testid="lead-detail-close" aria-label="关闭">
              <X size={15} />
            </button>
            <h2 className="pr-10 font-sans text-lg font-bold text-ink">{detail.name} <span className="text-sm font-normal text-ink/45">{detail.age}岁</span></h2>
            <div className="mt-4 space-y-4 text-sm">
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-moss">基本资料</p>
                <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2">
                  <dt className="text-ink/45">手机号码</dt><dd className="font-medium">{detail.phone || "—"}</dd>
                  <dt className="text-ink/45">咨询类型</dt><dd className="font-medium">{detail.topic}</dd>
                  <dt className="text-ink/45">目前情况</dt><dd className="font-medium">{detail.situation || "—"}</dd>
                  <dt className="text-ink/45">页面来源</dt><dd className="font-medium">{detail.source || "—"}</dd>
                </dl>
              </div>
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-moss">广告资料</p>
                <dl className="mt-2.5 space-y-2 break-all">
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">渠道</dt><dd className="font-medium">{detail.source_channel_label}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">page_path</dt><dd className="font-medium">{detail.page_path || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="shrink-0 text-ink/45">page_url</dt><dd className="text-right font-medium">{detail.page_url || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">utm_source</dt><dd className="font-medium">{detail.utm_source || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">utm_medium</dt><dd className="font-medium">{detail.utm_medium || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">utm_campaign</dt><dd className="font-medium">{detail.utm_campaign || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">gclid</dt><dd className="font-medium">{detail.gclid || "—"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-ink/45">fbclid</dt><dd className="font-medium">{detail.fbclid || "—"}</dd></div>
                </dl>
              </div>
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-moss">提交信息</p>
                <dl className="mt-2.5 space-y-2">
                  <div className="flex justify-between"><dt className="text-ink/45">lead_form_submit</dt><dd className="font-medium text-forest">已触发</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/45">whatsapp_click</dt><dd className={`font-medium ${detail.whatsapp_clicked ? "text-forest" : "text-ink/40"}`}>{detail.whatsapp_clicked ? "已跳转" : "未跳转"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/45">提交时间</dt><dd className="font-medium">{new Date(detail.created_at).toLocaleString("zh-CN", { hour12: false })}</dd></div>
                </dl>
              </div>
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-moss">跟进状态</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s} onClick={() => setStatusDraft(s)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${statusDraft === s ? "bg-forest text-paper" : "bg-paper text-ink/60 hover:bg-ink/5"}`} data-testid={`status-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="备注：例如「想了解一件代发，晚上再联系」"
                  rows={3}
                  className="mt-3 w-full rounded-xl border border-ink/15 bg-paper px-4 py-3 text-sm outline-none focus:border-forest"
                  data-testid="lead-notes"
                />
                <button onClick={saveDetail} disabled={saving} className="mt-3 w-full rounded-full bg-forest py-3 text-sm font-semibold text-paper hover:bg-ink disabled:opacity-60" data-testid="lead-save">
                  {saving ? "保存中…" : "保存状态和备注"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
